import { useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { cn, Icon, type IconName } from "@nexus/ui";
import { activeTasksQueryOptions, taskProgressKeys } from "@/lib/queries";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  TASK_PROGRESS_PANEL_ID,
  useTaskSidebarStore,
} from "@/features/task-progress/store/task-sidebar.store";
import { simulateTasks } from "@/features/task-progress/api/task-progress.api";
import { useTaskSidebarHover } from "@/features/task-progress/hooks/useTaskSidebarHover";
import {
  getTaskCounts,
  getTaskUrgency,
  formatTaskSummary,
  type TaskUrgency,
} from "@/features/task-progress/lib/task-counts";
import { config } from "@/config";
import { headerTriggerClassName } from "@/components/layout/header-trigger";

// Background work is ambient status, not an alert: the header trigger stays in the
// shared ghost family and the per-status glyph carries the colour. Only failure —
// the one state that needs a decision from the user — tints the whole control.
const FAILED_TRIGGER_TONE =
  "bg-error-subtle/60 text-error-subtle-foreground hover:bg-error-subtle";

const STATUS_GLYPH_TONE: Record<TaskUrgency, string> = {
  working: "text-muted-foreground",
  failed: "text-error",
  completed: "text-success",
};

function StatusCluster({
  icon,
  label,
  count,
  glyphTone,
  spinning,
}: {
  icon: IconName;
  label?: string;
  count: number;
  glyphTone: string;
  spinning?: boolean;
}) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <Icon
        name={icon}
        size="sm"
        color="inherit"
        className={cn(glyphTone, spinning && "animate-spin motion-reduce:animate-none")}
      />
      {label ? <span>{label}</span> : null}
      <span className="font-mono text-[12px] font-medium tabular-nums leading-none opacity-70">
        {count}
      </span>
    </span>
  );
}

export function TaskProgressIndicator() {
  const { t } = useTranslation();
  const userEmail = useAuth((s) => s.user?.email);
  const open = useTaskSidebarStore((s) => s.open);
  const toggle = useTaskSidebarStore((s) => s.toggle);
  const expand = useTaskSidebarStore((s) => s.expand);
  const dismissedIds = useTaskSidebarStore((s) => s.dismissedIds);
  const queryClient = useQueryClient();
  const { handleHoverOpen, handleHoverClose } = useTaskSidebarHover();
  const { data: allTasks = [] } = useQuery(activeTasksQueryOptions(userEmail ?? undefined));
  const tasks = allTasks.filter((task) => !dismissedIds.has(task.correlationId));

  // Auto-expand sidebar when new tasks appear (0 → N)
  const prevCountRef = useRef(tasks.length);
  useEffect(() => {
    if (prevCountRef.current === 0 && tasks.length > 0) {
      expand();
    }
    prevCountRef.current = tasks.length;
  }, [tasks.length, expand]);

  // Dev-only: Ctrl+Shift+T triggers task simulation
  const handleDevSimulate = useCallback(async () => {
    try {
      await simulateTasks();
      // Immediately refetch so the pill appears fast
      void queryClient.invalidateQueries({ queryKey: taskProgressKeys.all });
      expand();
    } catch {
      // Dev-only simulate; a failure here is not user-facing.
    }
  }, [queryClient, expand]);

  useEffect(() => {
    if (config.environment !== "development") return;
    const handler = (e: KeyboardEvent) => {
      if (e.ctrlKey && e.shiftKey && e.key === "T") {
        e.preventDefault();
        void handleDevSimulate();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [handleDevSimulate]);

  const counts = getTaskCounts(tasks);
  const { workingCount, failedCount, doneCount } = counts;
  const isIdle = tasks.length === 0;
  const urgency = getTaskUrgency(counts);
  const summary = formatTaskSummary(counts, (key, options) => t(key, options));
  const ariaLabel = open
    ? t("taskProgress.collapsePanel", "Collapse tasks panel — {{summary}}", { summary })
    : t("taskProgress.expandPanel", "Expand tasks panel — {{summary}}", { summary });

  // Hide the control entirely when there are no tasks. The hooks above still run
  // (auto-expand on 0→N, dev-simulate listener), so the control reappears as soon
  // as a task is discovered. Done/failed cards keep the list non-empty until dismissed.
  if (isIdle) return null;

  return (
    <div
      className="relative inline-flex"
      onMouseEnter={handleHoverOpen}
      onMouseLeave={handleHoverClose}
    >
      <button
        type="button"
        onClick={() => toggle()}
        aria-label={ariaLabel}
        aria-expanded={open}
        aria-controls={TASK_PROGRESS_PANEL_ID}
        data-task-sidebar-trigger=""
        data-urgency={urgency}
        className={headerTriggerClassName({
          open,
          className: cn(
            "relative",
            "before:absolute before:-inset-x-1 before:-inset-y-2.5 before:content-['']",
            "ease-out motion-reduce:transition-none",
            urgency === "failed" && FAILED_TRIGGER_TONE,
          ),
        })}
      >
        <span className="relative z-10 inline-flex items-center gap-2">
          {workingCount > 0 && (
            <StatusCluster
              icon="loader"
              label={urgency === "working" ? t("taskProgress.working", "Working") : undefined}
              count={workingCount}
              glyphTone={STATUS_GLYPH_TONE.working}
              spinning
            />
          )}
          {failedCount > 0 && (
            <StatusCluster
              icon="alert-circle"
              label={urgency === "failed" ? t("taskProgress.failed", "Failed") : undefined}
              count={failedCount}
              glyphTone={STATUS_GLYPH_TONE.failed}
            />
          )}
          {doneCount > 0 && (
            <StatusCluster
              icon="check-circle"
              label={urgency === "completed" ? t("taskProgress.complete", "Completed") : undefined}
              count={doneCount}
              glyphTone={STATUS_GLYPH_TONE.completed}
            />
          )}
          <Icon
            name="chevron-down"
            size="sm"
            color="inherit"
            className={cn(
              "opacity-40 transition-transform duration-150 ease-out motion-reduce:transition-none",
              open && "rotate-180",
            )}
          />
        </span>
      </button>
      <span className="sr-only" aria-live="polite" aria-atomic="true">
        {summary}
      </span>
    </div>
  );
}
